import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuthentication } from "../wrappers/UseAuthentication";
import { useSupplierRetailer } from "../wrappers/SupplierRetailerContext";
import { usePageAccess } from "../wrappers/PageAccessContext";

// Runs once on initial app load. If a refresh token is in localStorage,
// exchanges it for a fresh login payload via POST /login/refresh and drops
// the user into the app — replaces the older "replay plaintext password"
// behaviour. Falls back silently to the sign-in screen on any failure.
//
// Legacy fallback: rows that signed in before refresh tokens existed still
// have `username` + `password` in localStorage. We keep using those as a
// last-resort replay until the next successful sign-in upgrades them to a
// refresh token.
const useAutoSignIn = () => {
  const apiUrl = process.env.REACT_APP_API_URL;
  const navigate = useNavigate();
  const { signIn, isAuthenticated } = useAuthentication();
  const { fetchUserAccess } = useSupplierRetailer();
  const { fetchPageAccess, restrictedyn } = usePageAccess();
  const triedRef = useRef(false);

  useEffect(() => {
    if (triedRef.current) return;
    if (isAuthenticated) return;

    const staySignedIn = localStorage.getItem("staySignedIn") === "true";
    if (!staySignedIn) return;

    const refreshToken = localStorage.getItem("refreshToken");
    const legacyUsername = localStorage.getItem("username");
    const legacyPassword = localStorage.getItem("password");
    if (!refreshToken && !(legacyUsername && legacyPassword)) return;

    triedRef.current = true;

    const finishSignIn = async (data) => {
      const userid = data[0].userid;
      const fname = data[0].fname;
      const user_type = data[0].user_type || "default";
      localStorage.setItem("userid", userid);
      localStorage.setItem("fname", fname);
      localStorage.setItem("user_type", user_type);
      await fetchUserAccess(userid);
      await fetchPageAccess(userid);
      signIn(user_type);
      navigate(restrictedyn ? "/myProfile" : "/sales");
    };

    const run = async () => {
      // Preferred path: refresh token.
      if (refreshToken) {
        try {
          const r = await axios.post(`${apiUrl}/login/refresh`, { refreshToken });
          if (r.status === 201) {
            await finishSignIn(r.data.data);
            return;
          }
          // Server says token is invalid/expired — purge and stop.
          localStorage.removeItem("refreshToken");
        } catch (err) {
          // Network error: leave the token in place; user can sign in manually.
        }
      }

      // Legacy fallback path — only fires when there's no refresh token but
      // the older username/password pair is present from a pre-phase-6 session.
      if (legacyUsername && legacyPassword) {
        try {
          const r = await axios.post(`${apiUrl}/login/authenticate`, {
            username: legacyUsername,
            password: legacyPassword,
            remember: true,
          });
          if (r.status === 201) {
            if (r.data.refreshToken) {
              localStorage.setItem("refreshToken", r.data.refreshToken);
              // Plaintext password is no longer needed — purge it.
              localStorage.removeItem("password");
            }
            await finishSignIn(r.data.data);
          }
        } catch (err) {
          // Silent — user can sign in manually.
        }
      }
    };

    run();
    // Intentionally run once on mount only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};

export default useAutoSignIn;
